import { useEffect, useRef, useCallback, useState } from 'react'
import type { MonitorEventItem } from '@/lib/api'

const WS_BASE = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
const WS_URL = `${WS_BASE}/api/monitor/ws`
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000]

interface UseMonitorWSReturn {
  liveEvents: MonitorEventItem[]
  connected: boolean
  error: string | null
}

export default function useMonitorWS(enabled: boolean = true): UseMonitorWSReturn {
  const [liveEvents, setLiveEvents] = useState<MonitorEventItem[]>([])
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const retriesRef = useRef(0)
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const cancelledRef = useRef(false)

  const connect = useCallback(() => {
    if (!enabledRef.current) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    cancelledRef.current = false
    try {
      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        if (cancelledRef.current) {
          ws.close()
          return
        }
        setConnected(true)
        setError(null)
        retriesRef.current = 0
      }

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data) as MonitorEventItem
          setLiveEvents((prev) => {
            const next = [data, ...prev]
            return next.length > 200 ? next.slice(0, 200) : next
          })
        } catch {
          // ignore non-JSON messages
        }
      }

      ws.onerror = () => {
        setError('Connection error')
      }

      ws.onclose = () => {
        setConnected(false)
        wsRef.current = null
        if (enabledRef.current) {
          const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)]
          retriesRef.current += 1
          setTimeout(connect, delay)
        }
      }
    } catch (e) {
      setError(String(e))
    }
  }, [])

  useEffect(() => {
    if (enabled) {
      connect()
    }
    return () => {
      enabledRef.current = false
      cancelledRef.current = true
      const ws = wsRef.current
      if (ws) {
        if (ws.readyState === WebSocket.CONNECTING) {
          wsRef.current = null
          ws.onopen = () => ws.close()
        } else {
          ws.close()
          wsRef.current = null
        }
      }
    }
  }, [enabled, connect])

  return { liveEvents, connected, error }
}
